<template>
	<div class="pickUp-method-form">
		<div class="form-row">
			<div v-if="isAvailableNameSwitch" class="input-wrapper switch-wrapper">
				<div v-if="isAvailableNameSwitch && isUserNameValid" class="error-hint">
					姓名不能為空
				</div>
				<Input
					title="姓名"
					v-model="userName"
					:class="{ 'error-input': isUserNameValid }"
				/>
			</div>
			<div v-if="isAvailablePhoneSwitch" class="input-wrapper switch-wrapper">
				<div v-if="isAvailablePhoneSwitch && isPhoneValid" class="error-hint">
					電話不能為空
				</div>
				<div
					v-if="isAvailablePhoneSwitch && isPhoneFormatValid"
					class="error-hint"
				>
					電話格式不正確
				</div>
				<Input
					title="電話"
					v-model="phone"
					:class="{ 'error-input': isPhoneValid || isPhoneFormatValid }"
				/>
			</div>
		</div>
		<div class="form-row">
			<div class="input-wrapper pickup-time-wrapper" style="flex: 1">
				<div v-if="isPickUpTimeTypeValid" class="error-hint">
					取餐時間不能為空
				</div>
				<p class="pickup-time-label">取餐時間</p>
				<div
					class="pickup-time-radio-group"
					:class="{ 'error-input': isPickUpTimeTypeValid }"
				>
					<Radio
						v-if="isOnsiteTakeAvailable"
						name="NOW"
						v-model="pickUpTimeType"
						title="馬上取餐"
						cantToggle
						@change="handlePickupTimeTypeChange"
					/>
					<Radio
						v-if="isBookingAvailable"
						name="TIME"
						v-model="pickUpTimeType"
						title="預約取餐"
						cantToggle
						@change="handlePickupTimeTypeChange"
					/>
				</div>
			</div>
		</div>
		<div
			class="form-row"
			v-if="pickUpTimeType === 'TIME' && isBookingAvailable"
		>
			<div class="input-wrapper" style="flex: 1">
				<div v-if="isPickUpDateValid" class="error-hint">取餐時間不能為空</div>
				<ByVuePicker
					:data="displayPickupDateOptions"
					v-model="pickUpDate"
					placeholder="請選擇日期"
					:class="{ 'error-select': isPickUpDateValid }"
				/>
			</div>
		</div>
		<div
			class="form-row"
			v-if="pickUpTimeType === 'TIME' && isBookingAvailable"
		>
			<div class="input-wrapper" style="flex: 1">
				<ByVuePicker
					ref="pickUpTimePkr"
					:data="displayPickupTimeOptions"
					v-model="pickUpTime"
					placeholder="請選擇時間"
					:class="{ 'error-select': isPickUpDateValid }"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Input from '@/components/Input.vue'
import Radio from '@/components/Radio.vue'
import { getBookableTimeSlots, isTimeSlotSelectable } from '@/utils/booking'

export default {
	name: 'SelfForm',
	components: { Input, Radio },
	props: {
		errorValidate: {
			type: Object,
		},
		pickUpDraft: {
			type: Object,
		},
	},
	data() {
		return {
			userName: null,
			phone: null,
			pickUpTimeType: null,
			pickUpDate: null,
			pickUpTime: null,
		}
	},
	methods: {
		handlePickupTimeTypeChange(value) {
			// 當切換為馬上取餐時，清除日期和時間
			// 注意：由於使用 v-model，pickUpTimeType 已經自動更新
			if (value === 'NOW') {
				this.pickUpDate = null
				this.pickUpTime = null
			}
		},
		initSelfPickupForm() {
			const {
				pickUpMethod: selectedMethod,
				userName,
				userPhone,
				pickUpTimeType,
				pickUpDate,
				pickUpTime,
			} = this.$store.state.user
			if (selectedMethod && selectedMethod === 'SELF') {
				this.userName = userName
				// TODO: 需要統一 phone 為 usePhone
				this.phone = userPhone
				// 如果 store 中有取餐時間類型，使用 store 的值；否則預設為 'NOW'
				// 但如果 store 中的值是 'NOW' 但 NOW 選項不可用，則改為 'TIME'
				let timeType =
					pickUpTimeType || (this.isOnsiteTakeAvailable ? 'NOW' : 'TIME')
				if (timeType === 'NOW' && !this.isOnsiteTakeAvailable) {
					timeType = 'TIME'
				}
				this.pickUpTimeType = timeType
				this.pickUpDate = pickUpDate
				this.pickUpTime = pickUpTime
			} else {
				// 如果沒有選擇取餐方式，預設取餐時間類型
				if (!selectedMethod) {
					this.userName = userName
					this.phone = userPhone
				}
				// 預設為馬上取餐（如果可用），否則為指定時間
				this.pickUpTimeType = this.isOnsiteTakeAvailable ? 'NOW' : 'TIME'
			}
		},
	},
	computed: {
		isUserNameValid() {
			return this.errorValidate?.userName
		},
		isPhoneValid() {
			return this.errorValidate?.phone
		},
		isPhoneFormatValid() {
			return this.errorValidate?.phoneFormat
		},
		isPickUpTimeTypeValid() {
			return this.errorValidate?.pickUpTimeType
		},
		isPickUpDateValid() {
			return this.errorValidate?.pickUpDate
		},
		isAvailablePhoneSwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.self
				?.phoneSwitch
		},
		isAvailableNameSwitch() {
			return this.$store.state.store?.openInfo?.mobilePickupMethod?.self
				?.nameSwitch
		},
		isBookingAvailable() {
			return this.$store.getters.isBooking
		},
		isOnsiteTakeAvailable() {
			return this.$store.getters.isOnsiteTake && this.$store.getters.isOpen
		},
		displayPickupDateOptions() {
			const list = this.$store.state.store.openInfo.bookingTimeList
			// 無可預約日時後端回的是 []，只擋 null 會讓 picker 拿到 [[]] 而開出一片空白
			if (!list?.length) return [[{ label: '無可選項目', disabled: true }]]

			const weekday = ['日', '一', '二', '三', '四', '五', '六']

			const today = new Date()
			today.setHours(0, 0, 0, 0)

			const options = list.map(item => {
				const d = new Date(item.date)
				d.setHours(0, 0, 0, 0)

				const diff = Math.round((d - today) / 86400000)
				const prefix =
					diff === 0 ? '今天' : diff === 1 ? '明天' : diff === 2 ? '後天' : ''

				const label = `${prefix ? prefix + ' ' : ''}${
					d.getMonth() + 1
				}/${d.getDate()}(${weekday[d.getDay()]})`

				return {
					label,
					value: item.date,
					timeList: item.timeList,
				}
			})

			return [options] // 必須外包一層
		},
		displayPickupTimeOptions() {
			// 沒有選日期 → 回傳空欄位
			if (!this.pickUpDate) return [[{ label: '無可選項目', disabled: true }]]
			const min = this.$store.state.store.openInfo?.minimumBookInterval || 0
			const bookingList = this.$store.state.store.openInfo.bookingTimeList
			return getBookableTimeSlots(bookingList, this.pickUpDate, min)
		},
	},
	watch: {
		userName: {
			handler(newVal) {
				this.$emit('update:input', { userName: newVal })
			},
		},
		phone: {
			handler(newVal) {
				this.$emit('update:input', { phone: newVal })
			},
		},
		pickUpTimeType: {
			handler(newVal) {
				this.$emit('update:input', { pickUpTimeType: newVal })
			},
		},
		isOnsiteTakeAvailable: {
			handler() {
				this.initSelfPickupForm()
			},
		},
		isBookingAvailable: {
			handler() {
				this.initSelfPickupForm()
			},
		},
		pickUpDraft: {
			// 監聽父層傳入的值，渲染需保留的欄位
			immediate: true,
			deep: true,
			handler(val) {
				if (!val) return
				this.userName = val.userName ?? ''
				this.phone = val.phone ?? ''
			},
		},
		pickUpTime: {
			handler(newVal) {
				this.$emit('update:input', { pickUpTime: newVal })
			},
		},
		pickUpDate: {
			handler(newVal) {
				if (!newVal) {
					this.pickUpTime = null
					return
				}

				// 取用 picker 顯示中的同一份清單，避免各自算門檻而在跨分鐘邊界時不一致
				const timeList = this.displayPickupTimeOptions

				// 沿用仍可選的舊值：還原表單時是先設 pickUpDate 再設 pickUpTime，
				// 本 watcher 排到 nextTick 才跑，無條件覆寫會蓋掉還原的時間
				if (!isTimeSlotSelectable(timeList, this.pickUpTime)) {
					const firstSlot = timeList[0]?.find(
						item => item.value && !item.disabled,
					)
					this.pickUpTime = firstSlot?.value ?? null
				}
				this.$emit('update:input', { pickUpDate: newVal })
			},
		},
		displayPickupTimeOptions: {
			handler(newOptions) {
				if (!this.pickUpTime) return

				// 店家中途調整預約設定後，已選時間可能已不在清單中，畫面顯示空白但
				// 驗證照樣放行（後端只擋過去時間，不驗時段是否仍開放）。這裡只清空
				// 不自動遞補，讓使用者重選。
				if (!isTimeSlotSelectable(newOptions, this.pickUpTime)) {
					this.pickUpTime = null
					this.$alert('您選的取餐時間已無法預約，請重新選擇', 'warning')
				}
			},
		},
	},
	mounted() {
		this.initSelfPickupForm()
	},
}
</script>

<style lang="sass" scoped>
.form-row
  display: flex
  flex-wrap: wrap
  .switch-wrapper
    width: 48%
    @media screen and (max-width: 420px)
      width: 100%
    &:only-child
      width: 100%
.pickup-time-wrapper
  .pickup-time-label
    font-weight: bold
  .pickup-time-radio-group
    display: flex
    flex-direction: column
    gap: 10px
    padding: 10px 0
    &.error-input
      border: 1px solid red
      border-radius: 4px
      padding: 9px
.get-time-title
  font-size: 18px
  font-weight: bold
  padding: 10px 0 15px

.get-time-now
  margin-bottom: 15px

.get-time-time
  flex-wrap: wrap

.get-time-time-content
  width: 100%
  padding-top: 10px

.get-time-time-content-date-select
  margin-bottom: 10px
.error-select
  border: 2px solid red
  border-radius: 50px
</style>
